// Benchmark data for /photon/performance. The raw numbers are no longer
// edited here - they come from the release artifacts in
// data/photon/releases/*.json via components/photon/generated/release-data.ts
// (regenerate: pnpm photon:releases). This module re-exports the latest
// complete benchmark and the derived helpers the pages use.

import {
  BENCHMARKS as ALL_BENCHMARKS,
  RELEASE_MODELS,
} from "./generated/release-data";
import type {
  BaselineRuntime,
  BenchmarkDataset,
  BenchModel,
  BenchRuntime,
  ColdStartComparison,
  LatencyComparison,
  LatencyPercentiles,
  ThroughputCell,
} from "./release-types";

export type {
  BaselineRuntime,
  BenchModel,
  BenchRuntime,
  ColdStartComparison,
  LatencyComparison,
  LatencyPercentiles,
  ThroughputCell,
};

export type BenchmarkModality = "vision" | "speech";
// Every dataset, including throughput-only v4 benchmarks (ASR) - the page
// renders only the sections a dataset actually has.
export const BENCHMARKS = ALL_BENCHMARKS;

/** A dataset's input modality, from its models' manifest declarations. */
const datasetIsAudio = (benchmark: BenchmarkDataset) =>
  RELEASE_MODELS.find((m) => m.id === benchmark.models[0]?.id)
    ?.modalities.startsWith("Audio") ?? false;

export const benchmarkModality = (
  benchmark: BenchmarkDataset,
): BenchmarkModality => (datasetIsAudio(benchmark) ? "speech" : "vision");

export const latestBenchmarksForModality = (
  modality: BenchmarkModality,
) => {
  const matching = BENCHMARKS.filter(
    (benchmark) => benchmarkModality(benchmark) === modality,
  );
  const latestRelease = matching[0]?.id.split("-")[0];
  if (!latestRelease) return [];

  return matching.filter(
    (benchmark) =>
      benchmark.id === latestRelease ||
      benchmark.id.startsWith(`${latestRelease}-`),
  );
};

// The page tells exactly two stories, no dataset picker: the newest
// image+text benchmark, then the newest audio benchmark (BENCHMARKS is
// ordered newest release first).
export const LATEST_IMAGE_BENCHMARK = BENCHMARKS.find(
  (benchmark) => !datasetIsAudio(benchmark),
);
export const LATEST_AUDIO_BENCHMARK = BENCHMARKS.find(datasetIsAudio);

const getPrimaryBenchmark = () => {
  const benchmark = LATEST_IMAGE_BENCHMARK ?? LATEST_AUDIO_BENCHMARK;
  if (!benchmark) {
    throw new Error("Photon release data does not include a benchmark");
  }
  return benchmark;
};

/** The latest image benchmark used for product-wide summary claims. */
export const PRIMARY_BENCHMARK = getPrimaryBenchmark();

// ---- Latest complete benchmark environment (pages default to this) ----
export const BENCH_ENV = PRIMARY_BENCHMARK.environment;
export const BENCH_DISCLOSURES = BENCH_ENV.disclosures;
export const QUALITY_CAVEAT = BENCH_ENV.qualityCaveat;

export const RUNTIME_LABELS: Record<BenchRuntime, string> = {
  photon: BENCH_ENV.runtimes.photon,
  vllm: BENCH_ENV.runtimes.vllm,
  sglang: BENCH_ENV.runtimes.sglang,
};

export const BENCH_MODELS = PRIMARY_BENCHMARK.models;
export const BENCH_FAMILIES = PRIMARY_BENCHMARK.families;

export const THROUGHPUT_CELLS = PRIMARY_BENCHMARK.throughput;
export const LATENCY_ROWS = PRIMARY_BENCHMARK.latency;
export const COLD_START_ROWS = PRIMARY_BENCHMARK.coldStart;
export const RESOURCE_ROWS = PRIMARY_BENCHMARK.resources;

/** Percentage advantage of Photon over the baseline for one cell. */
export const throughputAdvantagePct = (cell: ThroughputCell) =>
  (cell.multiple - 1) * 100;

/** Photon's throughput as a multiple of the baseline for one cell. */
export const throughputMultiple = (cell: ThroughputCell) =>
  cell.multiple;

/** Tail spread (jitter indicator): p99 − p50, derived. */
export const latencySpread = (row: LatencyPercentiles) => row.p99 - row.p50;

/** Look up one pairwise cold-start comparison. */
export const coldStartComparison = (
  modelId: string,
  runtime: BaselineRuntime,
) => {
  const row = COLD_START_ROWS.find(
    (r) => r.modelId === modelId && r.runtime === runtime,
  );
  if (!row) throw new Error(`Missing cold-start row: ${modelId}/${runtime}`);
  return row;
};

/** Look up the display record for a benchmark model id. */
export const benchModelName = (modelId: string) => {
  const model = BENCH_MODELS.find((m) => m.id === modelId);
  if (!model) throw new Error(`Unknown benchmark model: ${modelId}`);
  return model;
};
